import { abrirSeletorDeData } from '../utils/abrirSeletorDeData';
import React, { useState } from 'react';
import { mascara, numero } from './ValorDoMes';
import { lerTaxa, taxaComoTexto, valorComoMascara } from './EditarDivida';
import { ParsedDebt, DebtType, DEBT_TYPE_LABEL } from '../services/api';
import './QuickEntry.css';
import { formatBRL } from '../utils/format';
import { LIMITES } from '../utils/limites';

/**
 * O card de confirmação de uma dívida interpretada.
 *
 * Existe pela mesma razão do `InvestmentProposal`: contratar dívida **não é
 * gasto**. O gasto são as parcelas, mês a mês. Sem este caminho, "fiz um
 * consignado de 53 mil em 36x" viraria um gasto de R$ 53.000 num mês só.
 *
 * A pergunta que mais importa aqui é a do desconto em folha, e ela é sutil o
 * bastante para merecer explicação:
 *
 * - **Descontado na folha** (consignado): a parcela sai do salário na origem.
 *   O salário que ela lança já vem líquido, então a parcela **nunca aparece em
 *   Transações** — lançá-la seria contar o mesmo desconto duas vezes.
 * - **Pago por fora** (empréstimo pessoal, financiamento): a parcela sai da
 *   conta e é um lançamento normal, como qualquer outro.
 *
 * Marcar errado faz a projeção errar por uma parcela inteira, todo mês, na
 * direção otimista — que é a cara.
 */

/** O que ainda impede de gravar. Exportado porque o pai desabilita o botão. */
export const debtIncomplete = (d: ParsedDebt | null): boolean =>
  !d || !d.installments || d.installments < 1 ||
  d.installmentValue == null || d.installmentValue <= 0 ||
  d.deductedFromSalary == null;

interface Props {
  debt: ParsedDebt;
  /** 'YYYY-MM-DD' da primeira parcela — o modelo não lê isso da frase. */
  primeiraParcela: string;
  onPrimeiraParcela: (d: string) => void;
  onChange: (next: ParsedDebt) => void;
  onSave: () => void;
  onCancel: () => void;
  salvando: boolean;
}

/*
 * Este `fmt` NÃO obedece ao olhinho, e a exceção é deliberada.
 *
 * É a tela de confirmar o que a pessoa ACABOU de escrever: "lançar R$ 187,40
 * no débito?". Esconder o número aqui tornaria a confirmação impossível — ela
 * estaria dizendo sim para um valor que não pode ler. A regra é a mesma do
 * campo de ajustar saldo: valor que está sendo digitado não se esconde.
 *
 * Mas a FORMATAÇÃO vem do dono único: era mais uma cópia do `Intl`.
 */
const fmt = (v: number) => formatBRL(v);

const TIPOS: DebtType[] = ['consignado', 'pessoal', 'financiamento', 'cartao', 'cheque-especial', 'outro'];

const DebtProposal: React.FC<Props> = ({
  debt, primeiraParcela, onPrimeiraParcela, onChange, onSave, onCancel, salvando,
}) => {
  // Texto do que ela digita: o número sozinho perderia "2," no meio da digitação.
  const [valor, setValor] = useState(valorComoMascara(debt.installmentValue));
  const [taxa, setTaxa] = useState(taxaComoTexto(debt.monthlyRate));
  const taxaInvalida = Number.isNaN(lerTaxa(taxa));
  const incompleto = debtIncomplete(debt) || !debt.name.trim() || taxaInvalida;
  const totalAPagar = debt.installments && debt.installmentValue != null
    ? debt.installments * debt.installmentValue
    : null;

  return (
    <div className="qe-card">
      <div className="qe-linha">
        <span className="qe-desc">{debt.name}</span>
        <span className="qe-valor">
          {debt.installments && debt.installmentValue != null
            ? `${debt.installments}× ${fmt(debt.installmentValue)}`
            : <em>parcelas a confirmar</em>}
        </span>
      </div>

      <div className="qe-chips">
        <span className="qe-chip">{DEBT_TYPE_LABEL[debt.type]}</span>
        {debt.totalAmount != null && <span className="qe-chip">contratado: {fmt(debt.totalAmount)}</span>}
        {totalAPagar != null && <span className="qe-chip">vai pagar: {fmt(totalAPagar)}</span>}
        {debt.deductedFromSalary === true && <span className="qe-chip">sai do salário</span>}
      </div>

      {/* Tipo: sempre visível, porque é ele que decide a pergunta seguinte. */}
      <div className="qe-gap">
        <div className="qe-gap-pergunta">Que tipo de dívida é?</div>
        <div className="qe-opcoes">
          {TIPOS.map(t => (
            <button
              key={t}
              className={`qe-opcao${debt.type === t ? ' sugerida' : ''}`}
              onClick={() => onChange({
                ...debt,
                type: t,
                // Consignado É descontado na folha — é o que a palavra
                // significa. Trocar PARA consignado responde a pergunta;
                // trocar para longe dela reabre.
                deductedFromSalary: t === 'consignado' ? true : debt.deductedFromSalary,
              })}
            >{DEBT_TYPE_LABEL[t]}</button>
          ))}
        </div>
      </div>

      {/* O que a IA entendeu, campo a campo e já preenchido (27/10). Antes só
          aparecia o que FALTAVA; o que ela entendeu errado (a taxa, quase
          sempre) só se corrigia apagando e registrando de novo. */}
      <div className="qe-gap">
        <div className="qe-gap-pergunta">
          Confira o que eu entendi
          <span className="qe-gap-evidencia">corrija aqui antes de registrar</span>
        </div>
        <div className="qe-opcoes dbp-campos">
          <label className="dbp-campo">
            <span>Nome</span>
            <input
              className="qe-criar-input" type="text" aria-label="Nome da dívida"
              value={debt.name} maxLength={LIMITES.nome}
              onChange={e => onChange({ ...debt, name: e.target.value })}
            />
          </label>
          <label className="dbp-campo">
            <span>Parcelas</span>
            <input
              className="qe-criar-input" style={{ width: 100 }} aria-label="Total de parcelas"
              type="number" min="1" step="1" placeholder="ex: 36" autoFocus={!debt.installments}
              value={debt.installments ?? ''}
              onChange={e => {
                const v = parseInt(e.target.value, 10);
                onChange({ ...debt, installments: Number.isFinite(v) && v >= 1 ? v : null });
              }}
            />
          </label>
          <label className="dbp-campo">
            <span>Valor da parcela</span>
            <input
              className="qe-criar-input" style={{ width: 140 }} aria-label="Valor da parcela"
              type="text" inputMode="numeric" placeholder="0,00"
              autoFocus={!!debt.installments && debt.installmentValue == null}
              value={valor}
              onChange={e => {
                const m = mascara(e.target.value);
                setValor(m);
                const v = m === '' ? NaN : numero(m);
                onChange({ ...debt, installmentValue: v > 0 ? v : null });
              }}
            />
          </label>
          <label className="dbp-campo">
            <span>Taxa (% ao mês)</span>
            <input
              className="qe-criar-input" style={{ width: 110 }} aria-label="Taxa ao mês"
              type="text" inputMode="decimal" placeholder="ex: 2,3"
              value={taxa}
              onChange={e => {
                setTaxa(e.target.value);
                const v = lerTaxa(e.target.value);
                if (!Number.isNaN(v)) onChange({ ...debt, monthlyRate: v });
              }}
            />
          </label>
        </div>
        {/* O app NÃO divide o total pelas parcelas. 53.000 em 36× não dá 1.472 —
            dá isso mais juros. Um número plausível e menor que o real some numa
            planilha e reaparece no fim do mês. */}
        {(debt.installmentValue == null || debt.installmentValue <= 0) && (
          <span className="qe-gap-evidencia">
            {debt.totalAmount != null && debt.installments
              ? `não dá para dividir ${fmt(debt.totalAmount)} por ${debt.installments}: isso ignoraria os juros e daria uma parcela menor que a real`
              : 'o valor que sai por mês, com juros (o do contrato)'}
          </span>
        )}
        {taxaInvalida && <span className="qe-gap-evidencia">a taxa é um número em % ao mês, como 2,3</span>}
      </div>

      {/* A pergunta cara. Os rótulos dizem a CONSEQUÊNCIA, não sim/não: é a
          diferença entre a parcela existir em Transações ou não existir.
          Fica sempre visível: o que a IA respondeu sozinha também pode estar errado. */}
      <div className="qe-gap">
        <div className="qe-gap-pergunta">
          Como a parcela é paga?
          <span className="qe-gap-evidencia">
            se sai do salário, você não vai lançar a parcela, porque ela já vem descontada
          </span>
        </div>
        <div className="qe-opcoes">
          <button
            className={`qe-opcao${debt.deductedFromSalary === true ? ' sugerida' : ''}`}
            aria-pressed={debt.deductedFromSalary === true}
            onClick={() => onChange({ ...debt, deductedFromSalary: true })}
          >
            descontada do meu salário
          </button>
          <button
            className={`qe-opcao${debt.deductedFromSalary === false ? ' sugerida' : ''}`}
            aria-pressed={debt.deductedFromSalary === false}
            onClick={() => onChange({ ...debt, deductedFromSalary: false })}
          >
            eu pago por fora
          </button>
        </div>
      </div>

      <div className="qe-gap">
        <div className="qe-gap-pergunta">
          Quando venceu (ou vence) a primeira parcela?
          <span className="qe-gap-evidencia">é a partir dela que o app conta quantas já foram</span>
        </div>
        <div className="qe-opcoes">
          <input
            className="qe-criar-input"
            style={{ width: 170 }}
            type="date"
            onClick={abrirSeletorDeData}
            value={primeiraParcela}
            onChange={e => onPrimeiraParcela(e.target.value)}
          />
        </div>
      </div>

      <div className="qe-acoes">
        <button className="qe-salvar" onClick={onSave} disabled={incompleto || salvando} aria-busy={salvando}>
          {incompleto ? 'Complete acima' : 'Registrar dívida'}
        </button>
        <button className="qe-cancelar" onClick={onCancel} disabled={salvando}>Cancelar</button>
      </div>
    </div>
  );
};

export default DebtProposal;
